import { Injectable } from '@angular/core';
import Utils from '@app/utils/utils';
import {
  SoftwareList,
  SoftwareVersionsObject
} from '@app/api/software-images-api.service';

export enum AcSoftwareType {
  AC = 'ac',
  AIC = 'aic',
  DRIVER = 'driver',
  WINDOWS_UPDATE = 'windowsUpdate'
}

export enum AcSoftwareId {
  AIC = 1,
  AC = 2,
  WINDOWS_UPDATE = 3,
  ADD_ON = 25
}

@Injectable()
export class AcSoftwarePageService {

  private acSoftwarePage: AcSoftwarePage;

  constructor() {
  }

  getAcSoftwarePage(softwareList) {
    if (!this.acSoftwarePage) {
      this.acSoftwarePage = new AcSoftwarePage(softwareList);
    }
    return this.acSoftwarePage;
  }

  /**
   * Set the update versions of software listed on the AC software page
   * @param versions Selected update versions
   */
  setAcSoftwareUpdates(versions: SoftwareVersionsObject) {
    if (Utils.isObjectEmpty(versions)) { return; }
    this.acSoftwarePage.openLabCds.updateVersion = this.getAcSoftwareVersion(
      versions['aicImageId'], AcSoftwareType.AIC);
    this.acSoftwarePage.acquisitionController.updateVersion = this.getAcSoftwareVersion(
      versions['acImageId'], AcSoftwareType.AC);
    this.acSoftwarePage.operatingSystem.updateVersion = this.getAcSoftwareVersion(
      versions['windowsUpdateId'], AcSoftwareType.WINDOWS_UPDATE);

    for (const driverSoftware of this.acSoftwarePage.drivers) {
      const driverId = driverSoftware.softwareId;
      const versionId = versions['driverIds'][driverSoftware.softwareId];
      const driverUpdate = this.getAcDriverVersion(driverId, versionId);
      // If the new update is undefined for the installed driver mark it
      // for uninstallation.
      if (!driverUpdate) {
        driverSoftware.shouldBeUninstalled = !!driverSoftware.activeVersion;
      }
      driverSoftware.updateVersion = driverUpdate;
    }
  }

  /**
   * Set the active versions of software listed on the AC software page
   * @param versions The software versions installed on the AC
   */
  setActiveAcSoftwareVersions(versions: SoftwareVersionsObject) {
    if (Utils.isObjectEmpty(versions)) {
      return;
    }
    if (versions.aicImageId) {
      this.acSoftwarePage.openLabCds.activeVersion = this.getAcSoftwareVersion(
        versions.aicImageId, AcSoftwareType.AIC);
    }
    if (versions.acImageId) {
      this.acSoftwarePage.acquisitionController.activeVersion = this.getAcSoftwareVersion(
        versions.acImageId, AcSoftwareType.AC);
    }
    if (versions.windowsUpdateId) {
      this.acSoftwarePage.operatingSystem.activeVersion = this.getAcSoftwareVersion(
        versions.windowsUpdateId, AcSoftwareType.WINDOWS_UPDATE);
    }
    if (versions.driverIds) {
      for (const driverSoftware of this.acSoftwarePage.drivers) {
        const driverId = driverSoftware.softwareId;
        const versionId = versions.driverIds[driverId];
        driverSoftware.activeVersion = this.getAcDriverVersion(driverId,
          versionId);
        driverSoftware.shouldBeUninstalled = false;

        // mark unsupported drivers
        const cdsActiveVersion = this.acSoftwarePage.openLabCds.activeVersion;
        if (cdsActiveVersion) {
          if (!(driverId in cdsActiveVersion.supportedDrivers)) {
            driverSoftware.isSupported = false;
          }
        }
      }
    }
    return this.acSoftwarePage;
  }

  getSoftwareSelections(): SoftwareVersionsObject {
    const updateVersions: SoftwareVersionsObject = {
      'acImageId': 0,
      'aicImageId': 0,
      'windowsUpdateId': 0,
      'driverIds': {}
    };
    updateVersions['acImageId'] = this.acSoftwarePage.acquisitionController.updateVersion.id;
    updateVersions['aicImageId'] = this.acSoftwarePage.openLabCds.updateVersion.id;
    updateVersions['windowsUpdateId'] = this.acSoftwarePage.operatingSystem.updateVersion.id;
    this.acSoftwarePage.drivers.forEach(
      (driverSoftware) => {
        const driverId = driverSoftware.softwareId;
        const updateVersion = driverSoftware.updateVersion;
        if (!updateVersion || driverSoftware.shouldBeUninstalled) {
          updateVersions['driverIds'][driverId] = null;
        } else {
          updateVersions['driverIds'][driverId] = updateVersion.id;
        }
      });

    return updateVersions;
  }

  getAcSoftwareVersion<T extends AcSoftwareVersion>(
    versionId: number, type: AcSoftwareType,
    softwareId: number = null): T {
    let version;
    let list;
    let data;
    const softwareList = this.acSoftwarePage.softwareList;

    if (type === AcSoftwareType.AIC) {
      list = softwareList['aicImages'] || [];
      data = list.find(software => software.id === versionId) || {};
      version = new OpenLabCdsVersion(data);
    }

    if (type === AcSoftwareType.AC) {
      list = softwareList['acImages'] || [];
      data = list.find(software => software.id === versionId) || {};
      version = new AcSoftwareVersion(data);
    }

    if (type === AcSoftwareType.WINDOWS_UPDATE) {
      list = softwareList['windowsUpdates'] || [];
      data = list.find(software => software.id === versionId) || {};
      version = new AcSoftwareVersion(data);
    }

    if (type === AcSoftwareType.DRIVER) {
      version = this.getAcDriverVersion(softwareId, versionId);
    }

    return version;
  }

  getSoftwareImageVersions(software: AcSoftware<AcSoftwareVersion>): {}[] {
    const softwareList = this.acSoftwarePage.softwareList;
    if (software) {
      if (software instanceof OpenLabCdsSoftware) {
        return softwareList.aicImages;
      }
      if (software instanceof AcquisitionControllerSoftware) {
        return this.getCompatibleAcImages(softwareList);
      }
      if (software instanceof OperatingSystemSoftware) {
        return this.getCompatibleWindowsUpdates(software, softwareList);
      }
      if (software instanceof AgilentDriverSoftware) {
        return this.getCompatibleDriversVersions(software.softwareId);
      }
    }
    return [];
  }

  getLatestAcImage() {
    const softwareList = this.acSoftwarePage.softwareList;
    const acImages = this.getCompatibleAcImages(softwareList)
    return acImages[acImages.length-1]
  }

  private getAcDriverVersion(driverId: number, versionId: number) {
    if (driverId === undefined) { return; }
    const driverList = this.acSoftwarePage.softwareList['drivers'] || [];
    const driverObj = driverList.find(obj => obj.id === driverId);
    const versionObj = driverObj.versions.find(
      driverVersion => driverVersion.id === versionId);

    if (versionObj) {
      return new AcSoftwareVersion(versionObj);
    }
  }

  /**
   * Returns a list of supported Windows update Ids for the base OS version.
   * @param baseOsVersion version described with string (1021H2)
   * @param windowsUpdates list of Windows updates on which the filtering is performed
   * @private
   */
  private getSupportedWindowsUpdateIds(baseOsVersion, windowsUpdates) {
    const supportedWindowsUpdates = windowsUpdates.filter(windowsUpdate => windowsUpdate.baseOsVersion === baseOsVersion);
    return supportedWindowsUpdates.map(supportedWindowsUpdate => supportedWindowsUpdate.id);
  }

  /**
   * Returns a list of pre-installed or supported  drivers versions for the
   * selected CDS version.
   * @param driverId Identifies the driver whose versions we want
   */
  private getCompatibleDriversVersions(driverId) {
    const selectedCdsUpdate = this.acSoftwarePage.openLabCds.updateVersion;
    const supportedDrivers = selectedCdsUpdate.supportedDrivers;
    const versionIds = supportedDrivers[driverId];
    const result = [];
    if (Array.isArray(versionIds)) {
      for (const versionId of versionIds) {
        const imageVersion = this.getAcDriverVersion(driverId, versionId);
        if (imageVersion) {
          result.push(imageVersion);
        }
      }
    }
    return result;
  }

  private getCompatibleWindowsUpdates(
    software: OperatingSystemSoftware, softwareList) {
    const result = [];
    let cdsVersion = this.acSoftwarePage.openLabCds.updateVersion;
    if (!cdsVersion) {
      cdsVersion = this.acSoftwarePage.openLabCds.activeVersion;
    }

    const aicImageBaseOsVersion = (cdsVersion.name.match(/\[(.*?)\]/) || [])[1];
    const supportedUpdates = this.getSupportedWindowsUpdateIds(aicImageBaseOsVersion, softwareList.windowsUpdates);

    for (const windowsUpdateId of supportedUpdates) {
      const supportedUpdate = this.getAcSoftwareVersion(windowsUpdateId,
        AcSoftwareType.WINDOWS_UPDATE, softwareList);

      if (software.activeVersion && software.activeVersion.name >
        supportedUpdate.name &&
        !this.acSoftwarePage.openLabCds.isUpdateRequired()) {
        supportedUpdate.isDisabled = true;
      }
      if (supportedUpdate.isDeleted == false || windowsUpdateId == this.acSoftwarePage.operatingSystem.updateVersion.id) {
        result.push(supportedUpdate);
      }
    }
    return result;
  }

  private getCompatibleAcImages(softwareList) {
    const result = [];
    const selectedCdsUpdate = this.acSoftwarePage.openLabCds.updateVersion;
    const supportedAcImageIds = selectedCdsUpdate.supportedAcImageIds;
    for (const acImageId of supportedAcImageIds) {
      const imageVersion = this.getAcSoftwareVersion(acImageId,
        AcSoftwareType.AC,
        softwareList);
      result.push(imageVersion);
    }
    return result;
  }

}

/**
 * An abstract class representing a software version that can be set for a given
 * ac software type.
 */
export class AcSoftwareVersion {
  private readonly _isVisible = true;
  private readonly _hasReleaseNotes: boolean;
  private readonly _releaseDate: string;
  private _name: string;
  private readonly _id: number;
  private _isDisabled = false;
  private _isDeleted = false;
  private readonly _testError: string;

   constructor(data) {
    this._name = data.name;
    this._id = data.id;
    this._releaseDate = data.releaseDate;
    this._hasReleaseNotes = data.hasReleaseNotes;
    this._isVisible = data.isVisible;
    this._testError = data.testError;
    this._isDeleted = data.is_deleted
  }

  get id(): number {
    return this._id;
  }

  get name(): string {
    return this._name;
  }

  get isDisabled(): boolean {
    return this._isDisabled;
  }

  get isDeleted(): boolean {
    return this._isDeleted;
  }

  set isDisabled(value: boolean) {
    this._isDisabled = value;
  }

  get isVisible(): boolean {
    return this._isVisible;
  }

  get hasReleaseNotes(): boolean {
    return this._hasReleaseNotes;
  }

  get releaseDate(): string {
    return this._releaseDate;
  }

  get testError(): string {
    return this._testError;
  }

  set name(value: string) {
    this._name = value;
  }

}

class OpenLabCdsVersion extends AcSoftwareVersion {

  private readonly _releaseTrainId: any;
  private readonly _preInstalledDrivers: {};
  private readonly _supportedDrivers: {};
  private readonly _supportedAcImageIds: number[];
  private readonly _preInstalledWindowsUpdateId: number;

  constructor(data) {
    super(data);
    this._preInstalledDrivers = data.preInstalledDrivers;
    this._preInstalledWindowsUpdateId = data.preInstalledWindowsUpdateId;
    this._releaseTrainId = data.releaseTrainId;
    this._supportedAcImageIds = data.supportedAcImageIds;
    this._supportedDrivers = data.supportedDrivers;
  }

  get releaseTrainId(): any {
    return this._releaseTrainId;
  }

  get preInstalledDrivers(): {} {
    return this._preInstalledDrivers;
  }

  get supportedDrivers(): {} {
    return this._supportedDrivers;
  }

  get preInstalledWindowsUpdateId(): number {
    return this._preInstalledWindowsUpdateId;
  }

  get supportedAcImageIds(): number[] {
    return this._supportedAcImageIds;
  }

  isDriverPreinstalled(driverId, versionId) {
    return this._preInstalledDrivers[driverId] === versionId;
  }

  isDefaultWindowsUpdate(windowsUpdateId) {
    return this.preInstalledWindowsUpdateId === windowsUpdateId;
  }

}

/**
 * An generic abstract class representing a single software type on the AC
 * software page.
 * Each Software type consists of two different versions i.e an active
 * version and a selected update version of the software.
 */
export abstract class AcSoftware<T extends AcSoftwareVersion> {

  private _activeVersion: T;
  private _updateVersion: T;
  private _isRequired = true;
  private _isSupported = true;
  private _shouldBeUninstalled = false;
  private _testError: string;

  abstract get type(): AcSoftwareType;

  abstract get softwareId(): number;

  abstract get name(): string;
  
  get updateVersion(): T {
    return this._updateVersion;
  }

  set updateVersion(updateVersion: T) {
    this._updateVersion = updateVersion;
  }

  get activeVersion(): T {
    return this._activeVersion;
  }

  set activeVersion(activeVersion: T) {
    this._activeVersion = activeVersion;
  }

  isInstalled() {
    return this.activeVersion && !!this.activeVersion.id;
  }

  set shouldBeUninstalled(value) {
    this._shouldBeUninstalled = value;
  }

  get shouldBeUninstalled() {
    return this._shouldBeUninstalled;
  }

  set testError(value) {
    this._testError = value;
  }

  get testError() {
    return this._testError;
  }

  get isSupported() {
    return this._isSupported;
  }

  set isSupported(value: boolean) {
    this._isSupported = value;
  }

  isUpdateRequired(): boolean {

    if (!this.isSupported) {
      return false;
    }

    if (!this.activeVersion && !this.updateVersion) {
      return false;
    }

    if (this.shouldBeUninstalled) {
      return true;
    }

    if (!this.activeVersion && this.updateVersion) {
      return true;
    }

    if (!this.updateVersion) {
      return false;
    }

    return this.activeVersion.id !== this.updateVersion.id;
  }

  get isRequired() {
    return this._isRequired;
  }

  set isRequired(value: boolean) {
    this._isRequired = value;
  }
}

/**
 * Represents the AC software page. The page consists of four types of
 * software (components)
 * 1. OpenLab CDS software
 * 2. Operating System software
 * 3. Acquisition Controller software
 * 4. Driver software (more than 1)
 *
 */
export class AcSoftwarePage {

  private readonly _softwareList: SoftwareList;
  private readonly _openLabCds: OpenLabCdsSoftware;
  private readonly _acquisitionController: AcquisitionControllerSoftware;
  private readonly _operatingSystem: OperatingSystemSoftware;
  private readonly _drivers: AgilentDriverSoftware [] = [];

  constructor(softwareList: SoftwareList) {
    this._softwareList = softwareList;
    this._openLabCds = new OpenLabCdsSoftware();
    this._acquisitionController = new AcquisitionControllerSoftware();
    this._operatingSystem = new OperatingSystemSoftware();

    for (const driver of softwareList.drivers) {
      this._drivers.push(
        new AgilentDriverSoftware(driver.name, driver.id, driver.isRequired, driver.isAddOn, driver.isCdsRegistrationRequired));
    }
  }

  get softwareList(): SoftwareList {
    return this._softwareList;
  }

  get openLabCds(): OpenLabCdsSoftware {
    return this._openLabCds;
  }

  get acquisitionController(): AcquisitionControllerSoftware {
    return this._acquisitionController;
  }

  get operatingSystem(): OperatingSystemSoftware {
    return this._operatingSystem;
  }

  get drivers(): AgilentDriverSoftware[] {
    return this._drivers;
  }

}

/**
 * Represents the OpenLab CDS software on the AC software page
 */
class OpenLabCdsSoftware extends AcSoftware<OpenLabCdsVersion> {

  get name() {
    return 'OpenLab CDS';
  }

  get softwareId() {
    return AcSoftwareId.AIC;
  }

  get type() {
    return AcSoftwareType.AIC;
  }

}

/**
 * Represents the Acquisition Controller software on the AC software page
 */
class AcquisitionControllerSoftware extends AcSoftware<AcSoftwareVersion> {

  get name() {
    return 'Linux Update';
  }

  get softwareId() {
    return AcSoftwareId.AC;
  }

  get type() {
    return AcSoftwareType.AC;
  }
}

/**
 * Represents the Operating System software on the AC software page
 */
class OperatingSystemSoftware extends AcSoftware<AcSoftwareVersion> {

  get name() {
    return 'Windows Update';
  }

  get softwareId() {
    return AcSoftwareId.WINDOWS_UPDATE;
  }

  get type() {
    return AcSoftwareType.WINDOWS_UPDATE;
  }

}

/**
 * Represents a given driver software on the AC software page
 */
export class AgilentDriverSoftware extends AcSoftware<AcSoftwareVersion> {
  private readonly _softwareId: number;
  private readonly _name: string;
  private readonly _isAddOn: boolean;
  private readonly _isCdsRegistrationRequired: boolean;

  constructor(driverName: string, softwareId: number, isRequired: boolean, isAddOn: boolean, isCdsRegistrationRequired: boolean) {
    super();
    this._name = driverName;
    this._softwareId = softwareId;
    this.isRequired = isRequired;
    this._isAddOn = isAddOn;
    this._isCdsRegistrationRequired = isCdsRegistrationRequired;
  }

  get name() {
    return this._name;
  }

  get softwareId(): number {
    return this._softwareId;
  }

  get type() {
    return AcSoftwareType.DRIVER;
  }

  get isAddOn() {
    return this._isAddOn;
  }

  get isCdsRegistrationRequired() {
    return this._isCdsRegistrationRequired;
  }
}